import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import usePortal from 'react-useportal';

import styled from 'styled-components';
import { useGlobalState } from '../state/globalStateContext';
import { clsx } from '../utils/utils';
import Modal from './Modal';

const ModalOverlayContainer = styled.div`
  --overlay-padding-vertical: var(--bigger-size);
  --overlay-padding-horizontal: calc(var(--x-small-size) / 2);
  transition: opacity 0.3s ease;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  padding: var(--overlay-padding-vertical) var(--overlay-padding-horizontal);
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  z-index: var(--z-index-modal);
  overflow: auto;
  pointer-events: none;
  opacity: 0;

  @media (min-width: 360px) {
    --overlay-padding-vertical: var(--bigger-size);
    --overlay-padding-horizontal: var(--bigger-size);
  }

  @media (min-width: 768px) {
    // WARNING: If changing this, adjust the maxPaddingUsed var in ModalOverlay.tsx
    --overlay-padding-vertical: 30px;
    --overlay-padding-horizontal: 40px;
  }

  &.visible {
    pointer-events: all;
    opacity: 1;
  }

  &.embedded {
    background-color: rgba($color: #000000, $alpha: 0.3);
  }
`;

const ModalContainer = styled.div`
  max-width: 100%;
  overscroll-behavior: contain;
  margin: auto 0;
  cursor: auto;

  .embedded & {
    position: absolute;
    max-width: calc(100% - 2 * var(--overlay-padding-horizontal));
  }
`;

const ModalWrapper = styled.div`
  transition: all 0.3s ease;
  transform: scale(0.5);

  .visible & {
    transform: scale(1);
  }

  .embedded & {
    // Disable scale transition to uncomplicate modal position calculations
    transform: scale(1);
    box-shadow: 0px 0px calc(var(--overlay-padding-horizontal) / 2) rgba(0, 0, 0, 0.6);
  }
`;
const ModalDecenterSpace = styled.div`
  content: '';
  height: 100px;
  width: 100%;
  display: block;
  cursor: pointer;
`;

export interface ModalOverlayProps {
  open: boolean;
  positionY?: number;
  className?: string;
  onClose: React.MouseEventHandler;
  children: React.ReactNode;
  renderWhenHidden?: boolean;
  modalClassName?: string;
}

const ModalOverlay = ({
  open,
  positionY,
  className,
  children,
  onClose,
  renderWhenHidden,
  modalClassName,
}: ModalOverlayProps): React.ReactElement => {
  const { isEmbedded } = useGlobalState();
  const { Portal } = usePortal();
  const rootRef = useRef<HTMLDivElement>(null);
  const [modalWrapperRef, setModalWrapperRef] = useState<HTMLDivElement | null>(null);
  const originalDocumentOverflow = useRef<null | string>(null);
  const [adjustedPositionY, setAdjustedPositionY] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (isEmbedded && typeof ResizeObserver !== 'undefined' && modalWrapperRef) {
      const handleResize = () => {
        if (modalWrapperRef && positionY) {
          const modalHeight = modalWrapperRef.getBoundingClientRect().height;
          const windowHeight = window.document.documentElement.getBoundingClientRect().height;
          const maxPaddingUsed = 30;
          setAdjustedPositionY(
            Math.min(
              Math.max(positionY, modalHeight / 2 + maxPaddingUsed),
              windowHeight - modalHeight / 2 - maxPaddingUsed,
            ),
          );
        }
      };
      const resizeObserver = new ResizeObserver(() => handleResize());

      resizeObserver.observe(modalWrapperRef);
      return () => {
        resizeObserver.disconnect();
      };
    }
    return;
  }, [isEmbedded, modalWrapperRef, positionY]);

  // The `open` prop only mean the component above wants to open the modal
  // but whether the modal is actually rendered and visible is controlled
  // by these two states. These are controlled in the useEffect below
  // in a way that allows for opening and closing animations.
  const [shouldRender, setShouldRender] = useState(!!renderWhenHidden);
  const [visible, setVisible] = useState(false);

  const previousOpenRef = useRef<boolean | null>(null);
  const skipNextOverlayClick = useRef(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose(event as unknown as React.MouseEvent);
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);

      originalDocumentOverflow.current = document.documentElement.style.overflow || '';
      document.documentElement.style.overflow = 'hidden';

      if (renderWhenHidden) {
        setVisible(true);
      } else {
        setShouldRender(true);
        // wait a bit before setting visible to true so that
        // opening animations run as the modal overlay gets
        // first rendered hidden and only then turned visible
        setTimeout(() => {
          setVisible(true);
        }, 50);
      }
    } else {
      if (originalDocumentOverflow.current !== null) {
        document.documentElement.style.overflow = originalDocumentOverflow.current || '';
        originalDocumentOverflow.current = null;
      }

      if (renderWhenHidden) {
        setVisible(false);

        if (previousOpenRef.current !== null && open !== previousOpenRef.current) {
          // Scroll to top of content on closing so it starts
          // from the top when opened again
          setTimeout(() => {
            if (rootRef.current) {
              rootRef.current.scrollTo(0, 0);
            }
          }, 500);
        }
      } else {
        setVisible(false);

        if (previousOpenRef.current !== null && open !== previousOpenRef.current) {
          // Wait a bit before we stop rendering to allow for
          // closing animations to run
          setTimeout(() => {
            setShouldRender(false);
          }, 300);
        }
      }
    }
    previousOpenRef.current = open;

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return shouldRender ? (
    <Portal>
      <ModalOverlayContainer
        className={clsx(visible && 'visible', isEmbedded && 'embedded', className)}
        onClick={e => {
          e.stopPropagation();
          if (skipNextOverlayClick.current) {
            skipNextOverlayClick.current = false;
          } else {
            onClose(e);
          }
        }}
      >
        <ModalContainer
          style={
            isEmbedded
              ? {
                  top: positionY ? adjustedPositionY + 'px' : '50%',
                  transform: 'translateY(-50%)',
                }
              : undefined
          }
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <ModalWrapper ref={setModalWrapperRef}>
            <Modal
              onClose={onClose}
              onMouseDown={() => (skipNextOverlayClick.current = true)}
              onMouseUp={() => (skipNextOverlayClick.current = false)}
              className={modalClassName}
            >
              {children}
            </Modal>
          </ModalWrapper>
          {!isEmbedded && <ModalDecenterSpace onClick={onClose}></ModalDecenterSpace>}
        </ModalContainer>
      </ModalOverlayContainer>
    </Portal>
  ) : (
    <></>
  );
};

export default ModalOverlay;
